import { type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { LoadingBlur, InfoBox } from '@ics-portal/guardian';
import { ModalDivided } from '@components/_atoms/ModalDivided/ModalDivided';
import { type ProjectFormAttributes } from '../../ProjectForm';
import { ProjectFormGeneralSection } from './components/ProjectFormGeneralSection';
import { ProjectFormSidebarSection } from './components/ProjectFormSidebarSection';
import { useProjectFormDetailsState } from './useProjectFormDetailsState';

const CREATE_INFO_ITEMS = [
    'Please complete all required fields marked with an asterisk (*).',
    'The project owner is automatically set as the Security Responsible. This field cannot be changed while creating a project. If you need to assign a different Security Responsible, open the project later and update it in the Edit Project window.',
    'The project type cannot be changed once the project is created.',
];

const EDIT_INFO_ITEMS = [
    'Project type can not be changed. To change Project type you need to start a new project.',
    'Security Responsible must be a project member or owner. If needed, add the user in Project Settings > Members first.',
];

interface ProjectFormDetailsProps {
    isEditMode?: boolean;
}

export function ProjectFormDetails({
    isEditMode,
}: ProjectFormDetailsProps): ReactNode {
    const { control, register } = useFormContext<ProjectFormAttributes>();

    const {
        pid,
        currentUser,
        usersLoading,
        milestonesLoading,
        relatedLoading,
        showStepLoadingOverlay,
        showDeprecated,
        hasDeprecatedTypes,
        projectTypeOptions,
        statusOptions,
        organizationOptions,
        projectMilestonesOptions,
        projectMembersOptions,
        validateMilestone,
        toggleDeprecatedTypes,
    } = useProjectFormDetailsState();
    let infoItems: string[] | undefined;
    if (isEditMode) {
        infoItems = EDIT_INFO_ITEMS;
    } else if (!pid) {
        infoItems = CREATE_INFO_ITEMS;
    }

    return (
        <div
            className="relative flex flex-column gap-3"
            style={{ minHeight: '30rem' }}
        >
            <LoadingBlur
                loading={showStepLoadingOverlay}
                variant="ripple"
                style={{ width: '100%' }}
            >
                {infoItems ? <InfoBox items={infoItems} /> : null}
                <ModalDivided>
                    <ProjectFormGeneralSection
                        isEditMode={isEditMode}
                        relatedLoading={relatedLoading}
                        milestonesLoading={milestonesLoading}
                        hasDeprecatedTypes={hasDeprecatedTypes}
                        showDeprecated={showDeprecated}
                        projectTypeOptions={projectTypeOptions}
                        projectMilestonesOptions={projectMilestonesOptions}
                        statusOptions={statusOptions}
                        organizationOptions={organizationOptions}
                        register={register}
                        control={control}
                        validateMilestone={validateMilestone}
                        onToggleDeprecated={toggleDeprecatedTypes}
                    />
                    <ProjectFormSidebarSection
                        pid={pid}
                        usersLoading={usersLoading}
                        currentUser={currentUser}
                        projectMembersOptions={projectMembersOptions}
                        control={control}
                    />
                </ModalDivided>
            </LoadingBlur>
        </div>
    );
}
